/*
 * Copyright 2024 The Kokabo Authors. All rights reserved.
 * Use of this source code is governed by a BSD-style
 * license that can be found in the LICENSE file.
 */

import {MaxExecutionMessagePayloadSize, Fragment, Base64Encoding} from "./ExecutionMessage.js";
import {Log} from "../utils/Log.js";

class Assembler {

    /**
     * @param {ExecutionMessage} firstMessage
     * @param {function(ExecutionMessage)} finalFunc
     * @param {number} [timeOut=5000]
     * @constructor
     */
    constructor(firstMessage, finalFunc, timeOut = 5000) {
        this.prototype = firstMessage.prototypeWithoutPayload();
        this.finalFunc = finalFunc;
        this.timeOut = timeOut;
        this.builder = [];
        this.fragments = [];
        const resume = this.addFragmentedMessage(firstMessage, true);
        if (resume === false) {
            throw new Error("interrupted on creation");
        }
    }

    /**
     * Fragment metadata is encoded inside callName to keep compatibility with the reference Go implementation.
     * Consumers must treat it as untrusted input: interruption/out-of-order fragments are valid failure modes.
     *
     * @param {ExecutionMessage} message
     * @param {boolean} shouldBeFirst
     * @return {boolean} false if the assembly should be interrupted.
     */
    addFragmentedMessage(message, shouldBeFirst = false) {


        const metadataString = message.callName.slice(Fragment.length);
        const decodedMetadataString = atob(metadataString);

        /**
         * Metadata schema mirrors the server side and is intentionally compact:
         * ```go
         * type Metadata struct {
         *    Encoding  string `json:"e"`
         *    FileMode  bool   `json:"f"`
         *    CallName  string `json:"c"`
         *    Size      int    `json:"s"`
         *    Index     int    `json:"x"`
         *    Nb        int    `json:"n"`
         *    Last      bool   `json:"l"`
         *    Interrupt bool   `json:"i"`
         * }
         *```
         * @type {{e: string, f: boolean, c:string, s:number, x:number, l:number, l:boolean, i:boolean}}
         */
        const metadata = JSON.parse(decodedMetadataString);
        if (shouldBeFirst && metadata.x > 0) {
            // Without the first fragment (index 0), we cannot reconstruct deterministic payload ordering.
            return false;
        }
        if (metadata.i === true) {
            return false;
        }
        if (metadata.x === 0) {
            this.builder.length = metadata.s;
        }
        const payload = metadata.e === Base64Encoding ? atob(message.payload) : message.payload;
        this.builder.splice(metadata.x * MaxExecutionMessagePayloadSize, payload.length, ...payload);
        if (metadata.l) {
            this.prototype.callName = metadata.c;
            this.finalizeAssembly();
        }
        return true;
    }

    /**
     * Finalize buffered fragments into a single payload so downstream code never observes partial frames.
     */
    finalizeAssembly() {
        const assembledPayload = this.builder instanceof Uint8Array ? new Uint8Array(this.builder) : this.builder.join('');
        const finalMessage = {
            ...this.prototype,
            payload: assembledPayload
        };
        try {
            this.finalFunc(finalMessage);
        } catch (error) {
            Log.error(`[Assembler.finalizeAssembly] Error finalizing assembly: ${error.message}`);
            throw error;
        }
    }
}

export {Assembler};